import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { AnalysisType } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { CallIntelMutations, CallIntelTypes } from '@frontend/api-call-intel';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Modal, ModalControlModalProps, Text, TextField, useAlert, useFormField } from '@frontend/design-system';
import { FIELD_MAPPING } from '../../utils/constant';

type SuggestionModalProps = {
  fieldType: string;
  modalProps: ModalControlModalProps;
  name?: string;
  onClose: () => void;
  call?: CallIntelTypes.CallRecord;
  isDemoAccount: boolean;
};

export const SuggestionModal: React.FC<SuggestionModalProps> = ({
  call,
  isDemoAccount,
  fieldType,
  modalProps,
  name = 'suggestion',
  onClose,
}) => {
  const { t } = useTranslation('analytics');
  const alert = useAlert();

  const fieldProps = useFormField({
    type: 'text',
    value: '',
  });

  const { analysisType, title, description } = useMemo(() => {
    const analysisType = Object.entries(FIELD_MAPPING).find(
      ([_, fieldConfig]) => fieldConfig.type === fieldType
    )?.[0] as keyof typeof FIELD_MAPPING | undefined;

    const field = analysisType ? FIELD_MAPPING[analysisType] : null;

    return {
      analysisType,
      title: field?.suggestion?.title,
      description: field?.suggestion?.description,
    };
  }, [fieldType]);

  const { mutate: createSuggestionApi } = CallIntelMutations.useCreateSuggestion({
    isDemoAccount,
    onSuccess: () => {
      onClose();
      alert.success(t('Successfully submitted suggestion!'));
    },
    onError: () => {
      alert.error(t('Error submitting suggestion! Please try again.'));
    },
  });

  const handleSubmit = () => {
    const value = fieldProps.value.trim();
    if (value && analysisType) {
      const user = getUser();

      createSuggestionApi({
        id: call?.id as string,
        locationId: call?.locationId as string,
        userId: user?.userID as string,
        suggestion: value,
        type: analysisType as unknown as AnalysisType, //TODO: Till we migrate to new types
      });
    }
  };

  const handleCancel = () => {
    fieldProps.onChange({ name, value: '' });
    onClose();
  };

  return (
    <Modal {...modalProps} maxWidth={400} onClose={handleCancel}>
      <Modal.Header onClose={handleCancel}>{title}</Modal.Header>
      <Modal.Body>
        <Text>{description}</Text>
        <TextField
          {...fieldProps}
          css={styles.textField}
          clearable
          hideErrorText
          label=''
          name={name}
          placeholder={t('Suggestion')}
        />
      </Modal.Body>
      <Modal.Actions
        disablePrimary={!fieldProps.value}
        onPrimaryClick={handleSubmit}
        onSecondaryClick={handleCancel}
        primaryLabel={t('Submit')}
        secondaryLabel={t('Cancel')}
      />
    </Modal>
  );
};

const styles = {
  textField: css`
    margin-top: ${theme.spacing(2)};
  `,
};
